<script lang="ts">
  import { apiService } from '../services/api';

  export let currentMapping: Record<string, string> = {};
  export let metadata: Record<string, any> = {};
  export let nexusDefinition: string = "NXem";
  export let instrument: string = "";
  export let experiment: string = "";
  export let fileId: string = "";
  export let model: string = "";
  export let onmappingUpdate: (detail: {
    updatedMapping: Record<string, string>;
    changedKeys: string[];
    removedKeys: { key: string; oldPath: string }[];
    nexusId: string;
  }) => void = () => {};

  // Chat state
  let chatMessage = '';
  let chatLoading = false;
  let assistantMessage = '';
  let changedKeys: Set<string> = new Set();
  let chatHistory: Array<{role: 'user' | 'assistant', content: string}> = [];

  async function sendChatMessage() {
    if (!chatMessage.trim() || chatLoading) return;

    const userMessage = chatMessage.trim();
    chatMessage = '';
    chatLoading = true;
    assistantMessage = '';

    chatHistory = [...chatHistory, { role: 'user', content: userMessage }];

    try {
      const response = await apiService.chatMapping({
        message: userMessage,
        current_mapping: currentMapping,
        metadata: metadata,
        nexus_definition: nexusDefinition,
        instrument: instrument,
        experiment: experiment,
        file_id: fileId,
        model: model,
      });

      // Track which keys changed and which were removed
      const changedKeysList: string[] = [];
      const removedKeysList: { key: string; oldPath: string }[] = [];

      for (const [key, newPath] of Object.entries(response.changes)) {
        if (newPath === null) {
          // Key was removed - store with old path for display
          const oldPath = currentMapping[key] || '';
          removedKeysList.push({ key, oldPath });
        } else {
          changedKeysList.push(key);
        }
      }

      changedKeys = new Set(changedKeysList);

      assistantMessage = response.assistant_message;
      chatHistory = [...chatHistory, { role: 'assistant', content: response.assistant_message }];

      // Dispatch event with updated mapping
      onmappingUpdate({
        updatedMapping: response.updated_mapping,
        changedKeys: changedKeysList,
        removedKeys: removedKeysList,
        nexusId: response.nexus_id,
      });

      // Clear highlights after 10 seconds
      setTimeout(() => {
        changedKeys = new Set();
      }, 10000);

    } catch (error) {
      console.error('Chat error:', error);
      assistantMessage = `Error: ${error instanceof Error ? error.message : String(error)}`;
      chatHistory = [...chatHistory, { role: 'assistant', content: assistantMessage }];
    } finally {
      chatLoading = false;
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendChatMessage();
    }
  }
</script>

<div class="chat-section">
  <div class="chat-header">
    <span class="chat-icon">💬</span>
    <span>Ask AI to correct mappings</span>
  </div>

  {#if chatHistory.length > 0}
    <div class="chat-history">
      {#each chatHistory as message}
        <div class="chat-message {message.role}">
          <span class="chat-role">{message.role === 'user' ? 'You' : 'AI'}:</span>
          <span class="chat-content">{message.content}</span>
        </div>
      {/each}
    </div>
  {/if}

  {#if assistantMessage && changedKeys.size > 0}
    <div class="assistant-response">
      <div class="assistant-badge">AI Response</div>
      <p>{assistantMessage}</p>
      <div class="changes-summary">
        Changed keys: {Array.from(changedKeys).join(', ')}
      </div>
    </div>
  {/if}

  <div class="chat-input-container">
    <textarea
      class="chat-input"
      bind:value={chatMessage}
      on:keydown={handleKeyDown}
      placeholder="e.g., 'Map AccelerationVoltage to /entry/instrument/beam/voltage' or 'Remove the mapping for ImageWidth'"
      disabled={chatLoading}
      rows="2"
    ></textarea>
    <button
      class="chat-send-button"
      on:click={sendChatMessage}
      disabled={chatLoading || !chatMessage.trim()}
    >
      {#if chatLoading}
        <span class="loading-spinner"></span>
      {:else}
        Send
      {/if}
    </button>
  </div>
</div>

<style>
  .chat-section {
    @apply mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200;
  }

  .chat-header {
    @apply flex items-center gap-2 text-sm font-medium text-purple-800 mb-3;
  }

  .chat-icon {
    @apply text-lg;
  }

  .chat-history {
    @apply mb-3 max-h-40 overflow-y-auto space-y-2;
  }

  .chat-message {
    @apply p-2 rounded text-sm;
  }

  .chat-message.user {
    @apply bg-blue-100 text-blue-900;
  }

  .chat-message.assistant {
    @apply bg-purple-100 text-purple-900;
  }

  .chat-role {
    @apply font-semibold mr-2;
  }

  .chat-content {
    @apply break-words;
  }

  .assistant-response {
    @apply mb-3 p-3 bg-green-50 border border-green-200 rounded-lg;
  }

  .assistant-badge {
    @apply inline-block px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded mb-2;
  }

  .assistant-response p {
    @apply text-sm text-green-900 mb-2;
  }

  .changes-summary {
    @apply text-xs text-green-700 font-mono;
  }

  .chat-input-container {
    @apply flex gap-2;
  }

  .chat-input {
    @apply flex-1 p-3 text-sm border border-purple-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent;
  }

  .chat-input:disabled {
    @apply bg-gray-100 cursor-not-allowed;
  }

  .chat-send-button {
    @apply px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-20;
  }

  .loading-spinner {
    @apply w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin;
  }
</style>
